import React from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Link, graphql, useStaticQuery } from "gatsby"
import { useTranslation } from "react-i18next"
import { analyticsAction } from "../utils/analytics"

const featureList = [
  {
    params: {
      image: "film/how-new/01.png",
      title: "Time and cost savings 🕒💰",
      add: "Few clicks instead of a hundred hours",
      text: "Filmustage’s advanced AI <b>streamlines every step of pre-production</b>, making workflows faster, simpler, and more cost-effective. Our platform transforms complex tasks into quick, efficient steps, keeping your project on track and within budget.",
      buttonLink: "",
      buttonText: "",
      buttonColor: "",
      buttonTarget: "",
      featureItems: [
        "<b>Automatically break down your script</b> into scenes and elements like cast, props, locations, costumes, and more <i>in just 2 minutes</i>.",
        "Create accurate <b>shooting schedules</b> and <b>DOOD reports</b> <i>with just a few clicks</i>.",
        "Organize your perfect shedule using <b>AI Sorting</b>.",
        "<b>Automatically build and manage your project budget</b> based on your breakdown, schedules, and report data to optimize resources and maintain financial control.",
        "Effortlessly generate <b>script synopses</b> with AI.",
        "Use AI tools to determine <b>the scope of the project</b> and streamline <b>resource allocation</b>.",
      ],
    },
  },

  {
    params: {
      image: "film/how-new/04.png",
      title: "A one-stop-shop filmmaking platform for your needs 📝🎬",
      add: "All processes in one place",
      text: 'Manage your entire production cycle in one seamless platform, eliminating the need to juggle multiple tools.<span class="br-spacer"></span>Easily integrate with industry-standard software whenever necessary.',
      buttonLink: "",
      buttonText: "",
      buttonColor: "",
      buttonTarget: "",
      featureItems: [
        "Enjoy <b>a unified space</b> for script breakdown, analysis, scheduling and budgeting.",
        "Export your results to other platforms like <b>Movie Magic</b>, <b>Final Draft</b>, and <b>Gorilla Scheduling</b>, as well as in convenient formats such as <b>PDF</b> or <b>XLSX</b>.",
        "Download your <b>budget as a spreadsheet complete with all formulas</b>, enabling seamless customization and edits in your preferred external tools.",
        "Ensure smooth collaboration across your production team with our <b>Team Access feature</b>.",
      ],
    },
  },

  {
    params: {
      image: "film/how-new/03.png",
      title: "Risk management 🔍🛡️",
      add: "Navigate the chaos like a pro!",
      text: 'Filming costs a lot. You must ensure everything is covered before shooting begins.<span class="br-spacer"></span>Filmustage will find the scenes where additional preparation is needed and highlight them for you.',
      buttonLink: "",
      buttonText: "",
      buttonColor: "",
      buttonTarget: "",
      featureItems: [
        "Get information about <b>animal involvement</b>, <b>environmental precautions</b>, <b>stunts appearance</b>, and so on.",
        "Find out if you need to apply <b>COVID-19 restrictions</b>.",
        "Ensure your scenes are clear for use with our AI's <b>copywriting clearance check</b>.",
        "Detect scenes requiring additional <b>safety measures</b> to guarantee on-set security.",
        "Analyze a scene with <b>your parameters</b>.",
      ],
    },
  },

  {
    params: {
      image: "film/how-new/02.png",
      title: "Easy to use 🖥️👌",
      add: "5 mins to start",
      text: "While most industry software can be complex and user-unfriendly, Filmustage stands out with its simplicity. We've designed it to be intuitive and straightforward, ensuring you can get started in just few minutes. And if you have any questions, we're here to help. <span class=\"br-spacer\"></span>Sign up to enjoy:",
      buttonLink:
        "https://app.filmustage.com/sign/up?fm_page=main&fm_cta=why_block",
      buttonText: "Try for Free 💨",
      buttonColor: "bg-fPurple-800 hover:bg-fPurple-900",
      buttonTarget: "",
      featureItems: [
        "<b>Simple UX</b> made by filmmakers for filmmakers.",
        "<b>24/7 live support</b> for subscribers.",
        "<b>Live demos</b> for you and your team with Filmustage representatives.",
      ],
    },
  },
]

function isExternalLink(url) {
  return url.startsWith("http://") || url.startsWith("https://")
}

function FeaturesZigzagList({ htmlStringData, translatedFeatures }) {
  const { t } = useTranslation("home")
  const data = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          extension: { in: ["jpeg", "jpg", "png"] }
        }
      ) {
        edges {
          node {
            relativePath
            childImageSharp {
              gatsbyImageData(
                width: 540
                height: 540
                quality: 80
                layout: CONSTRAINED
              )
            }
          }
        }
      }
    }
  `)

  // Build translated feature list for the home page
  const translatedFeatureList = [
    {
      params: {
        image: "film/how-new/01.png",
        title: t("features.timeCost.title") + " \uD83D\uDD52\uD83D\uDCB0",
        add: t("features.timeCost.add"),
        text: t("features.timeCost.text"),
        buttonLink: "",
        buttonText: "",
        buttonColor: "",
        buttonTarget: "",
        featureItems: t("features.timeCost.items", { returnObjects: true }),
      },
    },
    {
      params: {
        image: "film/how-new/04.png",
        title: t("features.oneStop.title") + " \uD83D\uDCDD\uD83C\uDFAC",
        add: t("features.oneStop.add"),
        text: t("features.oneStop.text"),
        buttonLink: "",
        buttonText: "",
        buttonColor: "",
        buttonTarget: "",
        featureItems: t("features.oneStop.items", { returnObjects: true }),
      },
    },
    {
      params: {
        image: "film/how-new/03.png",
        title:
          t("features.riskManagement.title") +
          " \uD83D\uDD0D\uD83D\uDEE1\uFE0F",
        add: t("features.riskManagement.add"),
        text: t("features.riskManagement.text"),
        buttonLink: "",
        buttonText: "",
        buttonColor: "",
        buttonTarget: "",
        featureItems: t("features.riskManagement.items", {
          returnObjects: true,
        }),
      },
    },
    {
      params: {
        image: "film/how-new/02.png",
        title:
          t("features.easyToUse.title") + " \uD83D\uDDA5\uFE0F\uD83D\uDC4C",
        add: t("features.easyToUse.add"),
        text: t("features.easyToUse.text"),
        buttonLink:
          "https://app.filmustage.com/sign/up?fm_page=main&fm_cta=why_block",
        buttonText: t("features.easyToUse.buttonText") + " \uD83D\uDCA8",
        buttonColor: "bg-fPurple-800 hover:bg-fPurple-900",
        buttonTarget: "",
        featureItems: t("features.easyToUse.items", { returnObjects: true }),
      },
    },
  ]

  let localFeatureList = translatedFeatureList

  // Use translated features from locale JSON if available (non-EN feature pages)
  if (translatedFeatures && translatedFeatures.length > 0) {
    localFeatureList = translatedFeatures.map(f => ({
      params: {
        image: f.image || "",
        title: f.title || "",
        add: f.add || "",
        text: f.text || "",
        buttonLink: f.buttonLink || "",
        buttonText: f.buttonText || "",
        buttonColor: f.buttonColor || "",
        buttonTarget: f.buttonTarget || "",
        featureItems: f.featureItems || [],
      },
    }))
  } else if (htmlStringData) {
    const featureRegex = /<ul class="feature_params">([\s\S]*?)<\/ul>/g
    const featureList = []

    let match
    while ((match = featureRegex.exec(htmlStringData)) !== null) {
      const featureContent = match[1]
      const lines = featureContent.split("\n").map(line => line.trim())
      const params = {}
      let insideFeatureItems = false
      let featureItems = []

      lines.forEach(line => {
        if (line.startsWith('<ul class="feature_items">')) {
          insideFeatureItems = true
        } else if (line.startsWith("</ul>") && insideFeatureItems) {
          insideFeatureItems = false
        } else if (insideFeatureItems) {
          const itemMatch = line.match(/<li>(.*?)<\/li>/)
          if (itemMatch) {
            featureItems.push(itemMatch[1].trim())
          }
        } else {
          const itemMatch = line.match(
            /<li class="feature_([a-zA-Z]+)">([\s\S]*?)<\/li>/,
          )
          if (itemMatch) {
            const key = itemMatch[1].trim()
            let value = itemMatch[2].trim()

            if (key === "image") {
              const imgMatch = value.match(/<img src="([^"]+)"[^>]*>/)
              value = imgMatch ? imgMatch[1].trim() : ""
            }

            params[key] = value
          }
        }
      })

      if (featureItems.length > 0) {
        params["featureItems"] = featureItems
      } else {
        params["featureItems"] = []
      }

      // Normalise to the empty string the locale branch above already produces. A block
      // that ships no `feature_image` line at all leaves the key UNSET, and `isExternalLink`
      // calls `.startsWith` on it while the list renders - which throws and takes the whole
      // page down, not just the block.
      params["image"] = params["image"] || ""

      // `image` is deliberately NOT required: a block without one renders as a full-width
      // band instead of a zigzag row, which is what a closing trust / enterprise section
      // wants - it is not a product feature and a screenshot beside it reads as though it
      // were. The other three ARE required, because a block missing its copy is a broken
      // block, and dropping it beats printing an empty row.
      if (params["title"] && params["add"] && params["text"]) {
        featureList.push({ params })
      }
    }

    localFeatureList = featureList
  }

  return (
    <div className="grid gap-20">
      {/* Items */}
      {localFeatureList.map((feature, index) => {
        const image = !isExternalLink(feature.params.image)
          ? data.allFile.edges.find(
              edge => edge.node.relativePath === feature.params.image,
            )?.node.childImageSharp.gatsbyImageData
          : false

        const isEven = index % 2 === 0
        // No image -> no zigzag: the copy takes the full twelve columns instead of leaving
        // an empty half where the screenshot would have been.
        const hasImage = Boolean(feature.params.image)

        const LinkText = () => {
          return (
            <>
              {feature.params.buttonText ? (
                <>{feature.params.buttonText}</>
              ) : (
                <>
                  {t("features.readMore")}{" "}
                  <span className="ml-1" role="img" aria-label="">
                    📖
                  </span>
                </>
              )}
            </>
          )
        }

        const linkColor = feature.params.buttonColor
          ? feature.params.buttonColor
          : "text-gray-100 bg-gray-700 hover:bg-gray-800"

        return (
          <div
            // Top-aligned, not centred. The image column is a fixed 540px square while the
            // copy column is whatever the copy happens to be, so `items-center` only looked
            // right when the two came out a similar height. Whenever the copy runs longer -
            // every block on the collaboration page, and just over half of the blocks across
            // the existing feature pages - it parked the screenshot in the middle of its
            // column with up to ~280px of dead space above it, which reads as a broken
            // layout. Sharing a top edge is also simply where the eye enters the block.
            // The trade is that a block whose image is TALLER than its copy now leaves that
            // slack below the text instead of splitting it - ordinary whitespace, not a hole.
            //
            // Both columns carry `mx-auto`, NOT `m-auto`. That is load-bearing: `m-auto` sets
            // auto margins on all four sides, and a grid item with auto margins in the block
            // axis absorbs the free space itself - auto margins beat align-self, so the item
            // centres and this class here does nothing at all. `m-auto` was what actually
            // centred these columns; the alignment property was never in play.
            className="md:grid md:grid-cols-12 md:gap-6 md:items-start"
            key={index}
          >
            {/* Image */}
            {hasImage && (
              <div
                className={`rounded-md shadow-2xl shadow-indigo-500/30 relative max-w-xl md:max-w-none md:w-full mx-auto md:col-span-5 lg:col-span-6 mb-8 md:mb-0 ${
                  isEven ? "md:order-1" : "rtl-grid"
                }`}
                data-aos="fade-up"
                data-aos-offset="75"
              >
                {image ? (
                  <GatsbyImage
                    image={getImage(image)}
                    alt={feature.params.title}
                    className="rounded-md opacity-95 max-w-full m-auto md:max-w-none w-full md:w-[540px] h-auto"
                  />
                ) : (
                  <img
                    className="rounded-md opacity-95 max-w-full m-auto md:max-w-none w-full md:w-[540px] h-auto"
                    src={feature.params.image}
                    width="540"
                    height="540"
                    alt={feature.params.title}
                  />
                )}
              </div>
            )}

            {/* Content */}
            <div
              className={`max-w-xl md:max-w-none md:w-full mx-auto ${
                hasImage ? "md:col-span-7 lg:col-span-6" : "md:col-span-12"
              }`}
              data-aos={
                hasImage ? (isEven ? "fade-right" : "fade-left") : "fade-up"
              }
              data-aos-offset="100"
            >
              <div
                className={
                  !hasImage
                    ? ""
                    : isEven
                      ? "md:pr-4 lg:pr-12 xl:pr-16"
                      : "md:pl-4 lg:pl-12 xl:pl-16"
                }
              >
                <div className="font-architects-daughter text-xl text-fOrange-900 mb-2">
                  {feature.params.add}
                </div>

                <h3 className="h3 mb-3">{feature.params.title}</h3>

                <p
                  className="text-xl text-gray-400 mb-4 feature_text"
                  dangerouslySetInnerHTML={{ __html: feature.params.text }}
                ></p>

                <ul className="text-lg text-gray-400 -mb-2 feature_items">
                  {feature.params.featureItems.map((item, index) => (
                    <li className="flex items-start mb-2" key={index}>
                      <svg
                        className="w-3 h-3 fill-current text-green-500 mr-2 mt-2 flex-shrink-0"
                        viewBox="0 0 12 12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                      </svg>

                      <span dangerouslySetInnerHTML={{ __html: item }}></span>
                    </li>
                  ))}
                </ul>

                {feature.params.buttonLink && (
                  <div
                    className=" pt-10 text-center md:text-left"
                    data-aos="fade-up"
                  >
                    {!isExternalLink(feature.params.buttonLink) ? (
                      <Link
                        className={`btn ${linkColor}`}
                        to={feature.params.buttonLink}
                      >
                        <LinkText />
                      </Link>
                    ) : (
                      <a
                        className={`btn ${linkColor}`}
                        href={feature.params.buttonLink}
                        target={
                          feature.params.buttonTarget
                            ? feature.params.buttonTarget
                            : "_self"
                        }
                        onClick={async () => {
                          if (
                            feature.params.buttonLink.includes(
                              "app.filmustage.com/sign/up",
                            )
                          )
                            await analyticsAction("landing_sign_up_click")
                        }}
                      >
                        <LinkText />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default FeaturesZigzagList
